<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>Transport Service Performance</v-card-title>
      <v-card-subtitle>ລາຍງານຜົນການບໍລິການຂົນສົ່ງ — route plan results, one row per planned visit</v-card-subtitle>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-menu
              v-model="dateFromMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.date_from"
                  clearable
                  dense
                  hide-details
                  label="ເລີ່ມວັນທີ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="filters.date_from" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="2"
          >
            <v-menu
              v-model="dateToMenu"
              :close-on-content-click="true"
              min-width="auto"
              offset-y
              transition="scale-transition"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.date_to"
                  clearable
                  dense
                  hide-details
                  label="ຫາວັນທີ"
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="filters.date_to" />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-autocomplete
              v-model="filters.driver_id"
              :items="drivers"
              :item-text="driverLabel"
              item-value="id"
              label="ຄົນຂັບລົດ"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-autocomplete
              v-model="filters.route_plan_id"
              :items="routePlans"
              item-text="name"
              item-value="id"
              label="ແຜນເສັ້ນທາງ"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="filters.district_id"
              :items="districts"
              item-text="name"
              item-value="id"
              label="ເມືອງ"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-autocomplete
              v-model="filters.village_id"
              :items="villages"
              :loading="loadingVillages"
              :disabled="!filters.district_id.length"
              item-text="name"
              item-value="id"
              label="ບ້ານ"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="ສະຖານະ"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-select
              v-model="filters.customer_type"
              :items="customerTypeOptions"
              label="ປະເພດລູກຄ້າ"
              dense
              outlined
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            class="d-flex justify-end"
          >
            <v-btn
              color="primary"
              :loading="loading"
              @click="applyFilters"
            >
              Apply
            </v-btn>
            <v-btn
              class="ml-2"
              color="success"
              :loading="exporting"
              @click="exportExcel"
            >
              Export
            </v-btn>
          </v-col>
        </v-row>
        <v-alert
          v-if="errorMessage"
          class="mt-3 mb-0"
          type="error"
          dense
          text
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
    </v-card>

    <v-row
      class="mb-4"
      dense
    >
      <v-col
        cols="12"
        sm="6"
        md="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">
              Planned Visits
            </div>
            <div class="text-h5">
              {{ formatNumber(summary.total_planned) }}
            </div>
            <div class="text-caption">
              {{ summary.date_from }} → {{ summary.date_to }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">
              Collected (Success)
            </div>
            <div class="text-h5 success--text">
              {{ formatNumber(summary.collected_count) }}
            </div>
            <div class="text-caption">
              Success rate: {{ summary.success_rate || 0 }}%
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">
              Not Collected
            </div>
            <div class="text-h5 error--text">
              {{ formatNumber(summary.not_collected_count) }}
            </div>
            <div class="text-caption">
              Bags: {{ formatNumber(summary.total_bags) }} · Containers: {{ formatNumber(summary.total_containers) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="text-overline">
              Coverage
            </div>
            <div class="text-body-2">
              Customers: {{ formatNumber(summary.distinct_customers) }}
            </div>
            <div class="text-body-2">
              Drivers: {{ formatNumber(summary.distinct_drivers) }}
            </div>
            <div class="text-body-2">
              Route plans: {{ formatNumber(summary.distinct_route_plans) }} · Plan days: {{ formatNumber(summary.distinct_plan_days) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="py-2">
            <span class="text-overline mr-2">By Status</span>
            <v-chip
              v-for="st in statusKeys"
              :key="st"
              class="mr-1 mb-1"
              :color="statusColor(st)"
              small
              dark
            >
              {{ statusLabel(st) }}: {{ formatNumber(byStatus(st)) }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [50, 100, 200, 500, 1000] }"
        dense
      >
        <template #item.status="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.status)"
            dark
          >
            {{ item.status_la || item.status }}
          </v-chip>
        </template>
        <template #item.customer_type="{ item }">
          {{ item.customer_type === 'company' ? 'ທຸລະກິດ' : 'ສາຍບ້ານ' }}
        </template>
        <template #item.is_pause="{ item }">
          {{ item.is_pause == 1 ? 'Yes' : '' }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import numberFormat from '../../Helpers/formatNumber';

const STATUS_LABELS = {
  pending: 'ລໍຖ້າເກັບ',
  success: 'ເກັບສຳເລັດ',
  wait_to_confirm: 'ເກັບແລ້ວ ລໍຖ້າຢືນຢັນ',
  canceled: 'ຍົກເລີກ',
  skip: 'ຂ້າມ',
  reject: 'ລູກຄ້າປະຕິເສດ',
};

const STATUS_COLORS = {
  pending: 'grey',
  success: 'green',
  wait_to_confirm: 'blue',
  canceled: 'red',
  skip: 'amber darken-2',
  reject: 'deep-orange',
};

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

export default {
  name: 'ReportRoutePlanResult',
  title() {
    return `Vientiane Waste Co-Dev|Transport Service Performance`;
  },
  data() {
    const today = new Date();
    const monthAgo = new Date(today);
    monthAgo.setDate(today.getDate() - 30);
    return {
      filters: {
        date_from: isoDate(monthAgo),
        date_to: isoDate(today),
        driver_id: [],
        route_plan_id: [],
        district_id: [],
        village_id: [],
        status: [],
        customer_type: null,
      },
      dateFromMenu: false,
      dateToMenu: false,
      drivers: [],
      routePlans: [],
      districts: [],
      villages: [],
      loadingVillages: false,
      statusKeys: Object.keys(STATUS_LABELS),
      statusOptions: Object.keys(STATUS_LABELS).map((value) => ({
        text: `${STATUS_LABELS[value]} (${value})`,
        value,
      })),
      customerTypeOptions: [
        { text: 'ສາຍບ້ານ (home)', value: 'home' },
        { text: 'ທຸລະກິດ (company)', value: 'company' },
      ],
      summary: {},
      items: [],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 100,
      },
      loading: false,
      exporting: false,
      errorMessage: '',
      headers: [
        { text: 'Date', value: 'plan_date' },
        { text: 'Driver', value: 'driver_name' },
        { text: 'Route Plan', value: 'route_plan_name' },
        { text: 'Round', value: 'round_in_day', align: 'right' },
        { text: 'Priority', value: 'priority', align: 'right' },
        { text: 'Customer ID', value: 'display_id' },
        { text: 'Customer', value: 'customer_name' },
        { text: 'Type', value: 'customer_type' },
        { text: 'Phone', value: 'phone' },
        { text: 'Village', value: 'village' },
        { text: 'District', value: 'district' },
        { text: 'Collection Type', value: 'collection_type' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Bags', value: 'bag', align: 'right' },
        { text: 'Containers', value: 'container', align: 'right' },
        { text: 'Collected At', value: 'collected_at' },
        { text: 'Paused', value: 'is_pause', align: 'center' },
        { text: 'Description', value: 'description' },
      ],
    };
  },
  watch: {
    'filters.district_id': function (districtIds) {
      this.reloadVillages(districtIds);
    },
    options: {
      handler(newVal, oldVal) {
        if (!oldVal) return;
        if (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          this.fetch();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.loadDrivers();
    this.loadRoutePlans();
    this.loadDistricts();
    this.fetch();
  },
  methods: {
    formatNumber(n) {
      return numberFormat(n || 0);
    },
    statusLabel(s) {
      return STATUS_LABELS[s] || s;
    },
    statusColor(s) {
      return STATUS_COLORS[s] || 'grey';
    },
    byStatus(s) {
      return (this.summary.by_status && this.summary.by_status[s]) || 0;
    },
    driverLabel(driver) {
      if (!driver) return '';
      const name = `${driver.name || ''} ${driver.surname || ''}`.trim();
      if (driver.vehicle && driver.vehicle.car_id) {
        return `${name} (${driver.vehicle.car_id})`;
      }
      return name;
    },
    buildParams() {
      const p = {};
      if (this.filters.date_from) p.date_from = this.filters.date_from;
      if (this.filters.date_to) p.date_to = this.filters.date_to;
      if (this.filters.driver_id.length) p.driver_id = this.filters.driver_id;
      if (this.filters.route_plan_id.length) p.route_plan_id = this.filters.route_plan_id;
      if (this.filters.district_id.length) p.district_id = this.filters.district_id;
      if (this.filters.village_id.length) p.village_id = this.filters.village_id;
      if (this.filters.status.length) p.status = this.filters.status;
      if (this.filters.customer_type) p.customer_type = this.filters.customer_type;
      return p;
    },
    applyFilters() {
      if (this.options.page !== 1) {
        // The options watcher triggers the fetch when the page resets.
        this.options.page = 1;
        return;
      }
      this.fetch();
    },
    async loadDrivers() {
      try {
        const res = await this.$axios.get('driver');
        this.drivers = (res.data && res.data.data) || [];
      } catch (error) {
        console.log(error);
      }
    },
    async loadRoutePlans() {
      try {
        const res = await this.$axios.get('route-plan', {
          params: { scope_for: 'all' },
        });
        this.routePlans = (res.data && res.data.data) || [];
      } catch (error) {
        console.log(error);
      }
    },
    async loadDistricts() {
      try {
        const res = await this.$axios.get('info/district', {
          params: { province_id: 1 },
        });
        this.districts = (res.data && res.data.data) || [];
      } catch (error) {
        console.log(error);
      }
    },
    async reloadVillages(districtIds) {
      if (!districtIds || !districtIds.length) {
        this.villages = [];
        this.filters.village_id = [];
        return;
      }
      this.loadingVillages = true;
      try {
        const lists = await Promise.all(
          districtIds.map((id) => this.$axios
            .get(`info/district/${id}/village`)
            .then((res) => (res.data && res.data.data) || [])
            .catch(() => []),
          ),
        );
        this.villages = lists.flat();
        const allowed = new Set(this.villages.map((v) => v.id));
        this.filters.village_id = this.filters.village_id.filter((id) => allowed.has(id));
      } finally {
        this.loadingVillages = false;
      }
    },
    async fetch() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const { data } = await this.$axios.get('v2/report-route-plan-result', {
          params: {
            ...this.buildParams(),
            page: this.options.page,
            per_page: this.options.itemsPerPage,
          },
        });
        this.summary = data.summary || {};
        this.items = (data.items && data.items.data) || [];
        this.total = (data.items && data.items.total) || 0;
      } catch (error) {
        console.log(error);
        this.errorMessage = this.describeError(error);
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      this.errorMessage = '';
      try {
        const response = await this.$axios.get('v2/report-route-plan-result/export', {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `route-plan-result-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        this.errorMessage = this.describeError(error);
      } finally {
        this.exporting = false;
      }
    },
    describeError(error) {
      const res = error && error.response;
      if (!res) return 'Network error — could not reach the server.';
      if (res.status === 403) return 'You do not have the report permission.';
      if (res.status === 422 && res.data && res.data.errors) {
        return Object.values(res.data.errors).flat().join(' ');
      }
      return (res.data && res.data.message) || `Request failed (${res.status}).`;
    },
  },
};
</script>
